/**
 * Canonical, persisted EO solve membership.
 *
 * Canvas visibility is presentation state, but excluding a component changes
 * the equations submitted to EO.  The latter is therefore model input and is
 * persisted independently from the React Flow graph.  Cycle-level toggles are
 * deliberately resolved to node ids at edit time: the exact solved subject
 * survives a later cycle-label/grouping change.
 */
import type { Edge, Node } from 'reactflow';

export const EO_SOLVE_MEMBERSHIP_VERSION = 1 as const;

export interface EoSolveMembershipV1 {
  version: typeof EO_SOLVE_MEMBERSHIP_VERSION;
  ignoredNodeIds: string[];
}

function decodeMembership(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  if (!value.trim()) return undefined;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * Return a deterministic exception list containing only ids present in the
 * saved graph.  Malformed/legacy-absent input fails to the backwards-compatible
 * default: every node participates in the solve.
 */
export function normalizeIgnoredNodeIds(
  value: unknown,
  nodes: readonly Pick<Node, 'id'>[],
): string[] {
  const decoded = decodeMembership(value);
  if (
    decoded
    && typeof decoded === 'object'
    && !Array.isArray(decoded)
    && 'version' in decoded
    && (decoded as { version?: unknown }).version !== EO_SOLVE_MEMBERSHIP_VERSION
  ) return [];
  const candidate = Array.isArray(decoded)
    ? decoded
    : decoded && typeof decoded === 'object' && !Array.isArray(decoded)
      ? (decoded as { ignoredNodeIds?: unknown }).ignoredNodeIds
      : undefined;
  if (!Array.isArray(candidate) || candidate.length === 0) return [];

  const validIds = new Set(nodes.map((node) => node.id));
  return [...new Set(candidate.filter(
    (id): id is string => typeof id === 'string' && id.length > 0 && validIds.has(id),
  ))].sort((left, right) => left.localeCompare(right));
}

export function normalizeEoSolveMembership(
  value: unknown,
  nodes: readonly Pick<Node, 'id'>[],
): EoSolveMembershipV1 {
  return {
    version: EO_SOLVE_MEMBERSHIP_VERSION,
    ignoredNodeIds: normalizeIgnoredNodeIds(value, nodes),
  };
}

export function serializeEoSolveMembership(
  value: unknown,
  nodes: readonly Pick<Node, 'id'>[],
): string {
  return JSON.stringify(normalizeEoSolveMembership(value, nodes));
}

/** Build the exact graph participating in EO from a persisted membership value. */
export function applyEoSolveMembership<N extends Node>(
  nodes: readonly N[],
  edges: readonly Edge[],
  value: unknown,
): { nodes: N[]; edges: Edge[]; membership: EoSolveMembershipV1 } {
  const membership = normalizeEoSolveMembership(value, nodes);
  if (membership.ignoredNodeIds.length === 0) {
    return { nodes: [...nodes], edges: [...edges], membership };
  }
  const ignored = new Set(membership.ignoredNodeIds);
  return {
    nodes: nodes.filter((node) => !ignored.has(node.id)),
    edges: edges.filter((edge) => !ignored.has(edge.source) && !ignored.has(edge.target)),
    membership,
  };
}
